import { type MixedDressingDetails } from '@sg/graphql-schema';

import { type CombinedDressingDetails, type Modification } from '../../types';
import { checkIfDressingIngredient } from './check-if-dressing-ingredient';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates combined dressing details from active modifications and mixed dressing details.
 */
export function generateCombinedDressingDetails(
  activeModifications: Modification[],
  mixedDressingDetails: MixedDressingDetails[],
): CombinedDressingDetails[] {
  const addDressingDetails = addDressingDetailsFactory(mixedDressingDetails);

  return activeModifications.reduce(addDressingDetails, []);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function addDressingDetailsFactory(
  mixedDressingDetails: MixedDressingDetails[],
) {
  return (
    allDressingsDetails: CombinedDressingDetails[],
    modification: Modification,
  ) => {
    const { ingredient, quantity } = modification;
    const isDressing = checkIfDressingIngredient(modification);

    if (!ingredient || !isDressing) {
      return allDressingsDetails;
    }

    const { id, name } = ingredient;

    const findDressingDetail = findDressingDetailFactory(id);
    const maybeDressingDetail = mixedDressingDetails.find(findDressingDetail);
    const weight = maybeDressingDetail?.weight;
    const portionsNumber = quantity ?? 1;
    const combinedDressingDetails = { id, name, weight, portionsNumber, modification }; // prettier-ignore

    return [...allDressingsDetails, combinedDressingDetails];
  };
}

function findDressingDetailFactory(id: string) {
  return (dressingDetails: MixedDressingDetails) =>
    dressingDetails.ingredientId === id;
}
